import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import Page from '../index'
import InfoBlock from '../../features/components/InfoBlock'
import SocialAuthChecker from '../../features/components/SocialAuthChecker'
import NewCampaignCreatedModal from '../../features/components/Modals/NewCampaignCreatedModal'
import CampaignsSummaryContent from './CampaignsSummaryContent'

import { getSelectedPlatform } from '../../helpers/handlePlatformSelection'

import { clearCampaign, getCampaign } from '../../modules/actions/campaigns'
import { clearLineItems } from '../../modules/actions/lineItems'
import { campaignErrorSelector, campaignWasLoadedSelector } from '../../modules/selectors/campaigns'
import { selectedAdAccountsSelector } from '../../modules/selectors/adAccounts'

import { ROUTES } from '../../constants/routes'
import { googleListItemInsights } from '../../constants/other'
import { GOOGLE_PLATFORM, PLATFORM_LABELS } from '../../constants/selectLists/platformList'

function CampaignsSummaryPage() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { campaign_id: campaignId } = useParams()

  const selectedPlatform = getSelectedPlatform()
  const campaignError = useSelector(campaignErrorSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const { id: selectedAdAccountId } = selectedAdAccount

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Partnerships',
        url: ROUTES.partnerships
      },
      {
        title: 'Campaign Hub',
        url: ROUTES.campaigns
      },
      {
        title: 'Campaign'
      }
    ],
    []
  )

  const getCampaignData = useCallback(() => {
    if (selectedAdAccountId && !campaignWasLoaded) {
      dispatch(
        getCampaign(
          {
            id: campaignId,
            account: selectedAdAccountId,
            ...(selectedPlatform === GOOGLE_PLATFORM && {
              insights: googleListItemInsights
            })
          },
          selectedPlatform
        )
      )
    }
  }, [dispatch, selectedPlatform, campaignId, selectedAdAccountId, campaignWasLoaded])

  useEffect(() => {
    return () => {
      dispatch(clearLineItems())
      dispatch(clearCampaign())
    }
  }, [dispatch])

  return (
    <Page helmetTitle="Campaigns" breadcrumbs={breadcrumbs}>
      {campaignError ? (
        <InfoBlock title={t('Sorry, we can’t find that campaign')} centered greyDescription>
          <div>{t('You might not have access to it, or it may have been deleted')}</div>
        </InfoBlock>
      ) : (
        <SocialAuthChecker
          platform={selectedPlatform}
          dataFetch={getCampaignData}
          adAccountId={selectedAdAccountId}
          authFailureText={`Please authenticate with ${PLATFORM_LABELS[selectedPlatform]} to retrieve the campaign details.`}
        >
          <CampaignsSummaryContent />

          <NewCampaignCreatedModal />
        </SocialAuthChecker>
      )}
    </Page>
  )
}

export default CampaignsSummaryPage
